import { InfoselSdkManager, Token } from '@infosel-sdk/core'
import { SecuritiesPricesSDK } from '@infosel-sdk/securities-prices'
import { User } from 'constants/types'
import { TSDKEviroment } from '../constants/types'

function userHasSdkCredentials(user: User): boolean {
  const access = user.token || user.accessToken || ''
  const refresh = user.refreshToken || ''
  return Boolean(access && refresh)
}

export class infoselSDK {
  private static instance: infoselSDK | null = null
  private securitiesPricesSDK: SecuritiesPricesSDK | null = null

  private constructor(user: User) {
    if (!userHasSdkCredentials(user)) {
      throw new Error('User not found')
    }

    const sdkManager = InfoselSdkManager.init({
      mode: String(import.meta.env.VITE_ENV) as TSDKEviroment,
      authProviderConfig: {
        type: 'existing-token',
        token: {
          accessToken: user.token || user.accessToken || '',
          refreshToken: user.refreshToken || ''
        }
      }
    })
    this.securitiesPricesSDK = new SecuritiesPricesSDK(sdkManager)
  }

  /**
   * Drops the singleton so the next call can build with a new session (login / logout).
   */
  public static resetInstance(): void {
    this.instance = null
  }

  /**
   * Returns the SDK only when {@link userSession} resolves to a user with tokens.
   * Returns `null` if the session is not ready yet (avoids throwing during Terminal bootstrap).
   */
  public static getSDK({ userSession }: { userSession: () => User | null }): infoselSDK | null {
    if (!userSession) {
      throw new Error('User session not found')
    }
    if (this.instance !== null) {
      return this.instance
    }
    const user = userSession()
    if (!user || !userHasSdkCredentials(user)) {
      return null
    }
    this.instance = new infoselSDK(user)
    return this.instance
  }

  public getSecuritiesPricesSDK(): SecuritiesPricesSDK {
    if (this.securitiesPricesSDK === null) {
      throw new Error('SDK not initialized')
    }
    return this.securitiesPricesSDK
  }

  public securitiesRefreshToken({ accessToken, refreshToken }: { accessToken: string; refreshToken: string }): void {
    const token: Token = {
      accessToken,
      refreshToken
    }
    this.securitiesPricesSDK?.sdkManager.authProvider.setToken(token)
  }
}
